import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { CommentForm, CommentsServiceContext, CreateComment, CommentList } from "@iventis/comments";
import { commentsApiFunctions } from "@iventis/comments-api";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { borderRadius, styled, media } from "@iventis/styles";
import { MapCommentCreateRequestDto } from "@iventis/domain-model/model/mapCommentCreateRequestDto";
import { Comment } from "@iventis/domain-model/model/comment";
import { Theme } from "@emotion/react";
import { COMMENT_VECTOR_SOURCE_ID, MapEventTypes, commentsLayerId, machineEventTypes } from "@iventis/map-engine";
import { mappingApi } from "@iventis/api/src/api";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Point } from "geojson";
import { AxiosResponse } from "axios";
import { ConfirmationDialogComponent } from "@iventis/components";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";
import { unwrapResult } from "@reduxjs/toolkit";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { ResourceType } from "@iventis/domain-model/model/resourceType";
import { useIsMobileUser } from "@iventis/utilities";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { RootDispatch, RootState } from "./state/root.store";
import { eventStream } from "./event-stream";
import { map } from "./state/map.slice";
import { getMapCommentFeature } from "./state/map.slice.thunks";
import { FROM_MAP_ROOT } from "./map-constants";

export const MapCommentsWithContext = forwardRef<
    { closeViewAllCommentsList(): void; commentsContainerRef: React.MutableRefObject<HTMLDivElement> },
    { className?: string; commentIsMoving: boolean; mapPermission: Authorisation; mapElementHeight: number; mapElementWidth: number }
>(({ className, commentIsMoving, mapPermission, mapElementHeight, mapElementWidth }, ref) => {
    const dispatch = useDispatch<RootDispatch>();

    const queryClient = useQueryClient();

    const isMobileUser = useIsMobileUser();

    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const commentsFormRef = useRef<HTMLDivElement>(null);

    const [showComment, setShowComment] = useState(true);
    const previousCoordinates = useRef<[number, number] | null>(null);
    const { user, selectedComments, mapId, geojson, hideComments } = useSelector((state: RootState) => ({
        user: state.auth.user,
        selectedComments: state.mapReducer.mapModule.commentsSelected.value,
        mapId: state.mapReducer.mapModule.mapId,
        geojson: state.mapReducer.mapModule.geoJSON.value,
        hideComments: state.mapReducer.mapModule.hideComments,
    }));

    const [viewAll, setViewAll] = useState(false);
    const [modal, setModal] = useState<{ type: "delete-confirmation"; commentId: string; resourceId: string } | null>(null);

    useImperativeHandle(ref, () => ({
        closeViewAllCommentsList: () => setViewAll(false),
        commentsContainerRef,
    }));

    const handleCreateComment = async (createComment: CreateComment, resourceId: string) => {
        // Find the map object for the comment
        const commentFeature = geojson[commentsLayerId].find((feature) => feature.objectId === createComment.id);
        dispatch(map.actions.setLocalToRemoteMapObjectsKey([{ key: createComment.id, value: createComment.id }]));
        const mappingServiceRequest = mappingApi.post<void, AxiosResponse<void>, MapCommentCreateRequestDto>(
            `maps/${resourceId}/comments`,
            {
                id: createComment.id,
                textContent: createComment.textContent,
                geometry: commentFeature.feature.geometry as Point,
                level: commentFeature.feature.properties.level,
                sitemapId: commentFeature.feature.properties.sitemapId,
            },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.map_comment) }),
                    message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.map_comment) }),
                },
            }
        );
        const commentServiceRequest = commentsApiFunctions.create(createComment, resourceId);
        await Promise.all([commentServiceRequest, mappingServiceRequest]);
        incrementMonitoringCount({ name: MetricName.COMMENT_CREATED, resourceId });
        // If the comments are hidden we want to deselect the comment after creation
        if (hideComments) {
            eventStream.next({
                type: machineEventTypes.selectComment,
                payload: undefined,
                from: FROM_MAP_ROOT,
            });
        }
    };

    const handleCommentClicked = async (commentId: string) => {
        if (isMobileUser) setViewAll(false);
        const response = await dispatch(getMapCommentFeature({ commentId, focus: true, panTo: true }));
        const mapComment = unwrapResult(response);
        eventStream.next({
            type: machineEventTypes.selectComment,
            payload: { commentId: mapComment.properties.id, userId: mapComment.properties.userId, position: mapComment.geometry.coordinates },
            from: FROM_MAP_ROOT,
        });
    };

    const deleteComment = async (commentId: string, resourceId: string) => {
        // Delete the comment from the map
        eventStream.next({ type: machineEventTypes.deleteComment, commentId });
        // Close the modal
        setModal(null);
        // If query cache is populated, remove the comment from the query cache
        if (queryClient.getQueryData(["comments", resourceId]) != null) {
            queryClient.setQueryData(["comments", resourceId], (oldData: Comment[]) => oldData.filter((comment) => comment.id !== commentId));
        }
        // Delete via the comments service
        await commentsApiFunctions.delete(commentId, resourceId);
        // Refresh the comment tiles source to remove the deleted comment
        const commentMapObject = geojson[commentsLayerId].find((feature) => feature.objectId === commentId);
        // If the comment map object is local we don't need to refresh the tiles
        if (commentMapObject == null) {
            eventStream.next({ type: MapEventTypes.REFRESH_SOURCE_TILES, payload: { sourceNames: [COMMENT_VECTOR_SOURCE_ID] } });
        }
    };

    const selectedCommentId = selectedComments[0]?.commentId;

    const commentsFormHeight = commentsFormRef.current?.clientHeight ?? 0;
    const commentsFormWidth = commentsFormRef.current?.clientWidth ?? 0;

    useEffect(() => {
        if (isMobileUser && !commentIsMoving) {
            setShowComment(true);
            return;
        }
        // When the comment starts moving we need to store the initial coordinates
        if (commentIsMoving) {
            previousCoordinates.current = selectedComments?.[0]?.canvasCoordinates;
            setShowComment(false);
        }

        // When the comment has finished moving we need to wait until the canvas position has updated until we show the comments
        if (
            !showComment &&
            previousCoordinates.current?.[0] !== selectedComments?.[0]?.canvasCoordinates?.[0] &&
            previousCoordinates.current?.[1] !== selectedComments?.[0]?.canvasCoordinates?.[1]
        ) {
            previousCoordinates.current = null;
            setShowComment(true);
        }
    }, [commentIsMoving, selectedComments?.[0]?.canvasCoordinates]);

    return (
        <div ref={commentsContainerRef} className={className}>
            <CommentsServiceContext.Provider
                value={{
                    ...commentsApiFunctions,
                    resource: { id: mapId, type: ResourceType.Map },
                    delete: async (commentId, resourceId) => setModal({ type: "delete-confirmation", commentId, resourceId }),
                    create: handleCreateComment,
                    loggedInUser: { id: user.userId, resourcePermission: mapPermission },
                    cancel: () => eventStream.next({ type: machineEventTypes.selectComment, payload: undefined, from: FROM_MAP_ROOT }),
                }}
            >
                {selectedCommentId != null && (
                    <StyledErrorBoundary>
                        <StyledCommentForm
                            key={selectedCommentId}
                            ref={commentsFormRef}
                            $commentIsMoving={commentIsMoving || !showComment}
                            $mapElementWidth={mapElementWidth}
                            $mapElementHeight={mapElementHeight}
                            $commentsFormHeight={commentsFormHeight}
                            $commentsFormWidth={commentsFormWidth}
                            selectedCommentId={selectedCommentId}
                            handleViewAllClicked={() => {
                                if (isMobileUser) setShowComment(false);
                                setViewAll(true);
                            }}
                            $positioning={selectedComments[0]?.canvasCoordinates}
                            isNewComment={selectedComments[0]?.isNewComment}
                        />
                    </StyledErrorBoundary>
                )}
                {viewAll && (
                    <StyledErrorBoundary onError={() => setViewAll(false)}>
                        <StyledCommentList
                            selectedCommentId={selectedCommentId}
                            handleCommentClick={handleCommentClicked}
                            handleCloseClick={() => {
                                if (isMobileUser) setShowComment(true);
                                setViewAll(false);
                            }}
                        />
                    </StyledErrorBoundary>
                )}
                <ConfirmationDialogComponent
                    cancelText={translate(Content.common.buttons.cancel)}
                    confirmText={translate(Content.common.buttons.delete)}
                    handleCancel={() => setModal(null)}
                    handleConfirm={() => deleteComment(modal?.commentId, modal?.resourceId)}
                    show={modal?.type === "delete-confirmation"}
                    title={translate(Content.comments.deleteConfirmation.title)}
                    message={translate(Content.comments.deleteConfirmation.description)}
                />
            </CommentsServiceContext.Provider>
        </div>
    );
});

const StyledErrorBoundary = styled(IventisErrorBoundaryWithToast)`
    background: ${({ theme }) => theme.secondaryColors.blank};
    width: 100%;
    height: 100%;
    border-radius: ${borderRadius.standard};
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
`;

const StyledCommentForm = styled(CommentForm)<{
    $commentIsMoving: boolean;
    $positioning: [number, number];
    $mapElementWidth: number;
    $mapElementHeight: number;
    $commentsFormHeight: number;
    $commentsFormWidth: number;
}>`
    visibility: ${({ $commentIsMoving }) => ($commentIsMoving ? "hidden" : "visible")};
    position: absolute;
    background-color: ${({ theme }) => theme.secondaryColors.blank};
    bottom: 0;
    left: 0;
    width: 100%;
    ${media.medium} {
        background-color: transparent;
        bottom: auto;
        ${({ $positioning, $mapElementWidth, $mapElementHeight, $commentsFormHeight, $commentsFormWidth }) =>
            `bottom: auto; 
             ${
                 $positioning[1] + $commentsFormHeight < $mapElementHeight ? `top: ${$positioning[1] - 25}px;` : `top: auto; bottom: ${$mapElementHeight - $positioning[1] - 25}px;`
             }  
             ${$positioning[0] + $commentsFormWidth < $mapElementWidth ? `left: ${$positioning[0] + 30}px;` : `left: auto; right: ${$mapElementWidth - $positioning[0] + 30}px;`}
            `}
        width: auto;
    }
    z-index: 500;
`;

const StyledCommentList = styled(CommentList)`
    position: absolute;
    bottom: 0;
    left: 0;
    height: calc(100dvh - 101px);
    width: 100%;
    ${media.medium} {
        bottom: auto;
        top: 70px;
        left: auto;
        right: 35px;
    }
    z-index: 500;
`;
